import { useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, 
  Gavel, 
  FileText, 
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
  Upload,
  X,
  File
} from 'lucide-react';

export default function CourtFiling() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const preselectedCase = searchParams.get('case') || '';
  
  const [formData, setFormData] = useState({
    caseNumber: preselectedCase,
    filingType: '',
    title: '',
    description: ''
  });
  const [files, setFiles] = useState<File[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get filing types
  const { data: filingTypes } = useQuery({
    queryKey: ['filing-types'],
    queryFn: () => apiClient.getFilingTypes()
  });

  // Get user's cases for dropdown (if not preselected)
  const { data: cases } = useQuery({
    queryKey: ['user-cases'],
    queryFn: () => apiClient.getCases(),
    enabled: !preselectedCase
  });

  const submitMutation = useMutation({
    mutationFn: () => apiClient.submitFiling(formData),
    onSuccess: () => {
      setSubmitted(true);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitMutation.mutate();
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Filing Submitted</h2>
          <p className="text-gray-600 mb-6">
            Your {formData.filingType} has been submitted for review. A Case Manager will review 
            and process your filing shortly.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="text-sm text-gray-600">
              <p><span className="font-medium">Case:</span> {formData.caseNumber}</p>
              <p><span className="font-medium">Type:</span> {formData.filingType}</p>
              <p><span className="font-medium">Title:</span> {formData.title}</p>
              {files.length > 0 && (
                <p><span className="font-medium">Attachments:</span> {files.length} file(s)</p>
              )}
            </div>
          </div>
          <div className="flex justify-center gap-4">
            <Link
              to={`/cases/${formData.caseNumber}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              View Case
            </Link>
            <button
              onClick={() => {
                setSubmitted(false);
                setFormData({ caseNumber: preselectedCase, filingType: '', title: '', description: '' });
                setFiles([]);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <Link
          to={preselectedCase ? `/cases/${preselectedCase}` : '/cases'}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          {preselectedCase ? 'Back to case' : 'Back to cases'}
        </Link>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary-100 rounded-lg">
            <Gavel className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Submit Court Filing</h1>
            <p className="text-gray-600">File a motion, brief, or other legal document</p>
          </div>
        </div>
      </div>

      {/* Filing Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Case Number */}
        <div>
          <label htmlFor="caseNumber" className="block text-sm font-medium text-gray-700 mb-1">
            Case Number <span className="text-red-500">*</span>
          </label>
          {preselectedCase ? (
            <input
              type="text"
              id="caseNumber"
              value={formData.caseNumber}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
            />
          ) : (
            <select
              id="caseNumber"
              value={formData.caseNumber}
              onChange={(e) => setFormData({ ...formData, caseNumber: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select a case...</option>
              {cases?.map((c) => (
                <option key={c.caseNumber} value={c.caseNumber}>
                  {c.caseNumber} - {c.title}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Filing Type */}
        <div>
          <label htmlFor="filingType" className="block text-sm font-medium text-gray-700 mb-1">
            Filing Type <span className="text-red-500">*</span>
          </label>
          <select
            id="filingType"
            value={formData.filingType}
            onChange={(e) => setFormData({ ...formData, filingType: e.target.value })}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select filing type...</option>
            {filingTypes?.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="e.g., Motion to Suppress Evidence Obtained Without Warrant"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description / Summary
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            placeholder="Provide a brief summary of the filing..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Attachments
          </label>
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragOver 
                ? 'border-primary-500 bg-primary-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const droppedFiles = Array.from(e.dataTransfer.files);
              setFiles(prev => [...prev, ...droppedFiles]);
            }}
          >
            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">
              Drag and drop files here, or{' '}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                browse
              </button>
            </p>
            <p className="text-xs text-gray-500 mt-1">
              PDF, Word documents, images (max 50MB each)
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              className="hidden"
              onChange={(e) => {
                if (e.target.files) {
                  setFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                }
              }}
            />
          </div>

          {/* Selected files list */}
          {files.length > 0 && (
            <div className="mt-3 space-y-2">
              {files.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 min-w-0">
                    <File className="h-4 w-4 text-gray-500 shrink-0" />
                    <span className="text-sm text-gray-700 truncate">{file.name}</span>
                    <span className="text-xs text-gray-500">
                      ({(file.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFiles(files.filter((_, i) => i !== index))}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Notice */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Submission Notice</p>
            <p className="mt-1">
              All filings are submitted for review by a Case Manager before being added to the 
              official court record. You will be notified once your filing has been reviewed.
            </p>
          </div>
        </div>

        {/* Submit Error */}
        {submitMutation.isError && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <p className="font-medium">Submission Failed</p>
              <p className="mt-1">
                {(submitMutation.error as Error)?.message || 'An error occurred while submitting your filing.'}
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Link
            to={preselectedCase ? `/cases/${preselectedCase}` : '/cases'}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={submitMutation.isPending || !formData.caseNumber || !formData.filingType || !formData.title}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Submit Filing
              </>
            )}
          </button>
        </div>
      </form>

      {/* Filer Information */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <FileText className="h-4 w-4" />
          <span>Filing as: <strong>{user?.name}</strong> ({user?.email})</span>
        </div>
      </div>
    </div>
  );
}
